import type { JSX } from "react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { useLiveQuery } from "dexie-react-hooks";
import { toast } from "sonner";
import type { CookSessionApiItem, RecipeApiItem } from "@recipevault/shared";
import { db } from "../../sync/db.js";
import { sync } from "../../sync/SyncService.js";
import { setFocusedSessionId } from "./focusedSession.js";
import { useOpenCookSessions } from "./useOpenCookSessions.js";
import { Alert, AlertDescription } from "../../ui/alert.js";

// ---------------------------------------------------------------------------
// localStorage ack helpers (per-session, best-effort)
// ---------------------------------------------------------------------------

function ackKey(sessionId: string): string {
  return `rv.wakeAck.${sessionId}`;
}

function isAcked(sessionId: string): boolean {
  try {
    return localStorage.getItem(ackKey(sessionId)) === "1";
  } catch {
    return false;
  }
}

function ackWakeUp(sessionId: string): void {
  try {
    localStorage.setItem(ackKey(sessionId), "1");
  } catch {
    /* private mode / quota — best effort */
  }
}

// ---------------------------------------------------------------------------
// Inner component — renders the banner for a specific session
// ---------------------------------------------------------------------------

interface BannerProps {
  session: CookSessionApiItem;
  onDismiss: () => void;
}

function WakeUpBannerInner({ session, onDismiss }: BannerProps): JSX.Element {
  const navigate = useNavigate();
  const recipe = useLiveQuery<RecipeApiItem | undefined>(
    () => db.recipes.get(session.recipeId),
    [session.recipeId],
  );

  const title = recipe?.title ?? "your recipe";

  async function handleContinue(): Promise<void> {
    try {
      await sync.resumeCookSessionOptimistic(session.id);
      setFocusedSessionId(session.id);
      void navigate("/cook/" + session.id);
    } catch (err) {
      toast.error("Could not resume session — please try again.");
      console.error("[WakeUpBanner] resume failed", err);
    }
  }

  function handleDismiss(): void {
    ackWakeUp(session.id);
    onDismiss();
  }

  return (
    <Alert className="flex items-start justify-between gap-3 border-amber-600/60 bg-amber-950/40 text-amber-100">
      <AlertDescription className="flex-1 text-sm text-amber-200">
        <span className="font-medium text-amber-100">{title}</span>
        {" — your wait is ready. Continue whenever you're ready."}
      </AlertDescription>
      <div className="flex items-center gap-2 shrink-0">
        <button
          type="button"
          onClick={() => { void handleContinue(); }}
          className="rounded-md bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-500 transition-colors"
        >
          Continue cooking
        </button>
        <button
          type="button"
          aria-label="Dismiss"
          onClick={handleDismiss}
          className="rounded-md p-1.5 text-amber-300 hover:text-amber-100 hover:bg-amber-800/40 transition-colors"
        >
          ×
        </button>
      </div>
    </Alert>
  );
}

// ---------------------------------------------------------------------------
// WakeUpBanner — finds the first eligible session and renders the inner banner
// ---------------------------------------------------------------------------

export function WakeUpBanner(): JSX.Element | null {
  const sessions = useOpenCookSessions();
  const [dismissed, setDismissed] = useState<string | null>(null);

  // First session that: is waiting, has wakeUpFiredAt, and has not been acked
  const target = sessions.find(
    (s) =>
      s.status === "waiting" &&
      s.wakeUpFiredAt != null &&
      s.id !== dismissed &&
      !isAcked(s.id),
  ) ?? null;

  if (!target) return null;

  return (
    <WakeUpBannerInner
      session={target}
      onDismiss={() => { setDismissed(target.id); }}
    />
  );
}
